/** An engine page: Cash Signals, Sales Signals and the next.
 *
 * Every word and number comes from the server (backend/chainlit/
 * <engine>.py), counted from the tenant's own tables with fixed SQL when
 * the page is opened and kept under the data version; this component only
 * lays the blocks out, with the Know your data renderers. English only,
 * like the rest of the product.
 *
 * Rich layout (2026-11-05, after Shady's note that the pages read as a
 * narrow report): the page uses the width it has, opens with a strip of
 * figures and lays its sections out two abreast on wide screens. A page
 * with its own body (Cash Signals' customers table) passes `render`.
 *
 * Socketless (App.tsx SOCKETLESS_ROUTES). Open to every company user whose
 * company feeds what the page reads; the server refuses admins.
 */
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import KpiStrip, { kpisFromSections } from '@/components/engine/Kpis';
import { EnginePage as Page, fetchEnginePage } from '@/components/engine/api';
import { Header } from '@/components/header';
import {
  Building,
  LangContext,
  SectionView,
  surface
} from '@/components/know-your-data/blocks';
import { SidebarProvider } from '@/components/ui/sidebar';

const POLL_MS = 4000;
const MAX_POLLS = 30;

export default function EnginePage({
  api,
  eyebrow,
  fallbackTitle,
  render
}: {
  /** The page's API path, e.g. /api/cash-engine. */
  api: string;
  /** The small label above the title, e.g. "Cash Signals". */
  eyebrow: string;
  /** The title while the page is still loading. */
  fallbackTitle: string;
  /** The page's own body; the default is the figures strip and the sections. */
  render?: (page: Page) => ReactNode;
}) {
  const [page, setPage] = useState<Page | null>(null);
  const [error, setError] = useState<string | null>(null);
  const polls = useRef(0);

  const load = useCallback(() => {
    setError(null);
    fetchEnginePage(api)
      .then((p) => {
        setPage(p);
        if (p.building && polls.current < MAX_POLLS) {
          polls.current += 1;
          window.setTimeout(load, POLL_MS);
        }
      })
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Could not load this page.')
      );
  }, [api]);

  useEffect(() => {
    polls.current = 0;
    setPage(null);
    load();
  }, [load]);

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full flex-col">
        <Header />
        <div className="flex-1 overflow-auto" id="engine-scroll">
          <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 sm:py-6">
            <div className="mb-5 min-w-0 space-y-1">
              <p
                className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: 'var(--sig-blue)' }}
              >
                {eyebrow}
              </p>
              <h1 className="text-2xl font-semibold leading-snug text-[var(--sig-ink)]">
                {page?.title ?? fallbackTitle}
              </h1>
              {page?.subtitle ? (
                <p className="text-sm text-muted-foreground">{page.subtitle}</p>
              ) : null}
            </div>

            {error ? (
              <div className="rounded-xl border p-4 text-sm" style={surface}>
                <p className="text-[var(--sig-ink)]">{error}</p>
                <button
                  type="button"
                  className="mt-2 text-sm font-medium text-primary hover:underline"
                  onClick={load}
                >
                  Try again
                </button>
              </div>
            ) : !page || page.building ? (
              <Building lang="en" building={!!page?.building} />
            ) : (
              <LangContext.Provider value="en">
                {render ? render(page) : <Body page={page} />}
              </LangContext.Provider>
            )}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

/** The sections of a page, two abreast on wide screens, each a card. */
export function SectionsGrid({ page }: { page: Page }) {
  const sections = page.sections ?? [];
  if (!sections.length) {
    return (
      <div
        className="rounded-xl border p-6 text-sm text-muted-foreground"
        style={surface}
      >
        {page.empty ?? 'There is not enough data yet to build this page.'}
      </div>
    );
  }
  return (
    <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
      {sections.map((s) => (
        <div
          key={s.id}
          className="min-w-0 rounded-xl border p-4 sm:p-5 [&>section]:border-t-0 [&>section]:pt-0"
          style={surface}
        >
          <SectionView section={s} />
        </div>
      ))}
    </div>
  );
}

function Body({ page }: { page: Page }) {
  const kpis = page.kpis?.length
    ? page.kpis
    : kpisFromSections(page.sections ?? []);
  return (
    <div className="space-y-5">
      <KpiStrip kpis={kpis} lead />
      <SectionsGrid page={page} />
    </div>
  );
}
